import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { apiFetch } from './api.js';
import { type ApiBrowseSummary, BROWSE_PAGE_SIZE, firstServerSort } from './browse-query.js';
import {
  type ApiContactReach,
  type ApiContactReveals,
  type ApiRoleFamily,
  appendContactIncludes,
} from './enrich.js';
import { type FilterParamEntries, appendFilterEntries, filterEntriesKey } from './filter-params.js';
import type { ApiProjectSource } from './projects.js';
import type { RecordScores } from './scores.js';

const PAGE_SIZE = 1000;
const RELATIONSHIP_PAGE_SIZE = 200;
const AUTO_PAGE_CAP = 2000;
// Keep an already-loaded list "fresh" for 5 min so returning to the page serves
// cached rows instantly instead of re-streaming every page.
const LIST_STALE_TIME_MS = 5 * 60_000;

// -- Wire types --------------------------------------------------------------

export type ApiAccountType =
  | 'developer'
  | 'architect'
  | 'gc'
  | 'engineer'
  | 'agency'
  | 'law_firm'
  | 'consultant'
  | 'landscape_architect'
  | 'other';

export type ApiContactStatus = 'enriched' | 'partial' | 'pending' | 'stale';

export type ApiContactRole =
  | 'developer_contact'
  | 'architect_contact'
  | 'engineer_contact'
  | 'gc_contact'
  | 'agency_pm'
  | 'attorney'
  | 'owner_contact'
  | 'other';

/** One of a contact's three most recent projects (account contacts route, include=recent_projects). */
export interface ApiContactRecentProject {
  project_id: string;
  project_name: string | null;
  primary_address: string;
  roles: ApiContactRole[];
}

export interface ApiContactSummary {
  id: string;
  account_id: string;
  account_name: string;
  account_website: string | null;
  account_types: ApiAccountType[];
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  status: ApiContactStatus;
  enrichment_attempted_at: string | null;
  roles: ApiContactRole[];
  projects_count: number;
  created_at: string;
  updated_at: string;
  /** This workspace's Prospect Score; an older API omits it. */
  scores?: RecordScores;
  /** Present with include=reveals (PR5), include=reach and include=recent_projects (PR7); an older API omits them. */
  reveals?: ApiContactReveals;
  reach?: ApiContactReach;
  recent_projects?: ApiContactRecentProject[];
}

/** The detail read's free profile facts (include=reach); an older API omits them. */
export interface ApiContactDetail extends ApiContactSummary {
  headline?: string | null;
  location?: string | null;
  about?: string | null;
  current_role?: {
    title: string | null;
    employer_name: string | null;
    started_on: string | null;
    description: string | null;
  } | null;
}

export interface ApiContactProject {
  project_id: string;
  project_name: string | null;
  primary_address: string;
  city: string;
  state: string;
  stage:
    | 'pre_filing'
    | 'filed'
    | 'under_review'
    | 'approved'
    | 'permitted'
    | 'under_construction'
    | 'completed'
    | null;
  disposition: 'active' | 'denied' | 'withdrawn' | 'expired' | 'stalled' | 'cancelled';
  roles: ApiContactRole[];
  last_signal_at: string | null;
  latest_source: ApiProjectSource | null;
}

export interface ApiProjectContact {
  id: string;
  account_id: string;
  account_name: string;
  account_website: string | null;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  status: ApiContactStatus;
  roles: ApiContactRole[];
  updated_at: string;
}

interface PaginationBlock {
  limit: number;
  offset: number;
  total: number | null;
  next_cursor?: string | null;
  has_more?: boolean;
  as_of?: string;
}

export interface ApiContactListResponse {
  contacts: ApiContactSummary[];
  pagination: PaginationBlock;
}

export interface ApiContactDetailResponse {
  contact: ApiContactDetail;
}

export interface ApiContactProjectsResponse {
  projects: ApiContactProject[];
  pagination: PaginationBlock;
}

export interface ApiProjectContactsResponse {
  contacts: ApiProjectContact[];
  pagination: PaginationBlock;
}

export interface ApiAccountContactsResponse {
  contacts: ApiContactSummary[];
  pagination: PaginationBlock;
  /** Present with include=reach: the firm type's personas, in sort=persona order. */
  personas?: ApiRoleFamily[];
  /** Present when exclude_agency is passed: visible agency staff at the firm, shown or not. */
  agency_staff_count?: number;
  /** Present with include=reach: when the provider was last asked for people at the firm, within 30 days. */
  people_searched_at?: string | null;
}

// -- Helpers -----------------------------------------------------------------

function useAutoFetchNextPage(
  enabled: boolean,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  fetchNextPage: () => Promise<unknown>,
  isPlaceholderData = false,
) {
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      void fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData]);
}

// -- Hooks -------------------------------------------------------------------

function useLegacyWorkspaceContacts(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(workspaceId) && (options?.enabled ?? true);
  const infinite = useInfiniteQuery({
    queryKey: ['contacts', workspaceId, filterEntriesKey(filter), staticViewId ?? null],
    enabled,
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    staleTime: LIST_STALE_TIME_MS,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(PAGE_SIZE),
        offset: String(pageParam),
      });
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, filter);
      appendContactIncludes(qs);
      return apiFetch<ApiContactListResponse>(`/contacts?${qs.toString()}`);
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.contacts.length, 0);
      if (lastPage.contacts.length < PAGE_SIZE) return undefined;
      if (loaded >= AUTO_PAGE_CAP) return undefined;
      return loaded;
    },
  });

  useAutoFetchNextPage(
    enabled,
    infinite.hasNextPage,
    infinite.isFetchingNextPage,
    infinite.fetchNextPage,
    infinite.isPlaceholderData,
  );

  const contacts = useMemo(
    () => (infinite.data?.pages ?? []).flatMap((page) => page.contacts),
    [infinite.data?.pages],
  );
  const total = infinite.data?.pages[0]?.pagination.total ?? null;
  const data = useMemo(
    () => ({
      contacts,
      pagination: { limit: PAGE_SIZE, offset: 0, total },
    }),
    [contacts, total],
  );

  return useMemo(
    () => ({
      data,
      isLoading: infinite.isLoading,
      isPlaceholderData: infinite.isPlaceholderData,
      isError: infinite.isError,
      isFetching: infinite.isFetching || infinite.isFetchingNextPage,
      fetchNextPage: infinite.fetchNextPage,
      hasNextPage: infinite.hasNextPage,
      isFetchingNextPage: infinite.isFetchingNextPage,
      isFetchNextPageError: infinite.isFetchNextPageError,
    }),
    [
      data,
      infinite.isError,
      infinite.isFetching,
      infinite.isFetchingNextPage,
      infinite.isFetchNextPageError,
      infinite.fetchNextPage,
      infinite.hasNextPage,
      infinite.isLoading,
      infinite.isPlaceholderData,
    ],
  );
}

const CONTACT_BROWSE_SORTS = ['name', 'updatedAt', 'prospectScore'] as const;
const CONTACT_BROWSE_FALLBACK = { id: 'updatedAt', desc: true };

function useCursorWorkspaceContacts(
  workspaceId: string | undefined,
  filter: FilterParamEntries | undefined,
  staticViewId: string | undefined,
  sorting: SortingState,
  enabled: boolean,
) {
  const { sort, direction } = firstServerSort(
    sorting,
    CONTACT_BROWSE_FALLBACK,
    CONTACT_BROWSE_SORTS,
  );
  const infinite = useInfiniteQuery({
    queryKey: [
      'contacts-browse',
      workspaceId,
      filterEntriesKey(filter),
      staticViewId ?? null,
      sort,
      direction,
    ],
    enabled: Boolean(workspaceId) && enabled,
    initialPageParam: null as string | null,
    staleTime: LIST_STALE_TIME_MS,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(BROWSE_PAGE_SIZE),
        sort,
        direction,
      });
      if (pageParam) qs.set('cursor', pageParam);
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, filter);
      appendContactIncludes(qs);
      return apiFetch<ApiContactListResponse>(`/contacts?${qs.toString()}`);
    },
    getNextPageParam: (lastPage) => lastPage.pagination.next_cursor ?? undefined,
  });

  const contacts = useMemo(
    () => (infinite.data?.pages ?? []).flatMap((page) => page.contacts),
    [infinite.data?.pages],
  );
  const data = useMemo(
    () => ({
      contacts,
      pagination: infinite.data?.pages[0]?.pagination ?? {
        limit: BROWSE_PAGE_SIZE,
        offset: 0,
        total: null,
      },
    }),
    [contacts, infinite.data?.pages],
  );

  return useMemo(
    () => ({
      data,
      isLoading: infinite.isLoading,
      isPlaceholderData: false,
      isError: infinite.isError,
      isFetching: infinite.isFetching,
      fetchNextPage: infinite.fetchNextPage,
      hasNextPage: infinite.hasNextPage,
      isFetchingNextPage: infinite.isFetchingNextPage,
      isFetchNextPageError: infinite.isFetchNextPageError,
    }),
    [
      data,
      infinite.fetchNextPage,
      infinite.hasNextPage,
      infinite.isError,
      infinite.isFetchNextPageError,
      infinite.isFetching,
      infinite.isFetchingNextPage,
      infinite.isLoading,
    ],
  );
}

export interface WorkspaceContactsOptions {
  enabled?: boolean;
  browseMode?: boolean;
  sorting?: SortingState;
}

export function useWorkspaceContacts(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: WorkspaceContactsOptions,
) {
  const enabled = options?.enabled ?? true;
  const browseMode = options?.browseMode ?? false;
  const legacy = useLegacyWorkspaceContacts(workspaceId, filter, staticViewId, {
    enabled: enabled && !browseMode,
  });
  const cursor = useCursorWorkspaceContacts(
    workspaceId,
    filter,
    staticViewId,
    options?.sorting ?? [],
    enabled && browseMode,
  );

  return browseMode ? cursor : legacy;
}

export function useContactBrowseSummary(
  workspaceId: string | undefined,
  filter?: FilterParamEntries,
  staticViewId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      'contact-browse-summary',
      workspaceId,
      filterEntriesKey(filter),
      staticViewId ?? null,
    ],
    enabled: Boolean(workspaceId) && (options?.enabled ?? true),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '' });
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, filter);
      return apiFetch<ApiBrowseSummary>(`/contact-browse/summary?${qs.toString()}`);
    },
  });
}

/**
 * Tiny unpaginated lookup powering the filter bar's record typeahead.
 * Callers are expected to debounce `q` — every distinct trimmed value past
 * 2 chars is a new query key and fires a request.
 */
export function useContactSearchPreview(
  workspaceId: string | undefined,
  q: string,
  scopeFilter?: FilterParamEntries,
  staticViewId?: string,
) {
  const trimmed = q.trim();
  const previewFilter = [
    ...(scopeFilter ?? []).filter(([key]) => key !== 'q'),
    ['q', trimmed] as const,
  ];
  return useQuery({
    queryKey: [
      'contacts-preview',
      workspaceId,
      filterEntriesKey(previewFilter),
      staticViewId ?? null,
    ],
    enabled: Boolean(workspaceId) && trimmed.length >= 2,
    staleTime: 10_000,
    queryFn: async () => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '', limit: '5' });
      if (staticViewId) qs.set('view_id', staticViewId);
      appendFilterEntries(qs, scopeFilter);
      qs.set('q', trimmed);
      return apiFetch<ApiContactListResponse>(`/contacts?${qs.toString()}`);
    },
  });
}

export function useContactDetail(contactId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['contact-detail', contactId, workspaceId],
    enabled: Boolean(contactId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = appendContactIncludes(new URLSearchParams({ workspace_id: workspaceId ?? '' }));
      return apiFetch<ApiContactDetailResponse>(
        `/contacts/${encodeURIComponent(contactId ?? '')}?${qs.toString()}`,
      );
    },
  });
}

export function useContactProjects(contactId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['contact-projects', contactId, workspaceId],
    enabled: Boolean(contactId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(RELATIONSHIP_PAGE_SIZE),
      }).toString();
      return apiFetch<ApiContactProjectsResponse>(
        `/contacts/${encodeURIComponent(contactId ?? '')}/projects?${qs}`,
      );
    },
  });
}

export function useProjectContacts(projectId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['project-contacts', projectId, workspaceId],
    enabled: Boolean(projectId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(RELATIONSHIP_PAGE_SIZE),
      }).toString();
      return apiFetch<ApiProjectContactsResponse>(
        `/projects/${encodeURIComponent(projectId ?? '')}/contacts?${qs}`,
      );
    },
  });
}

export function useAccountContacts(accountId: string | null, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['account-contacts', accountId, workspaceId],
    enabled: Boolean(accountId) && Boolean(workspaceId),
    queryFn: async () => {
      const qs = new URLSearchParams({
        workspace_id: workspaceId ?? '',
        limit: String(RELATIONSHIP_PAGE_SIZE),
      }).toString();
      return apiFetch<ApiAccountContactsResponse>(
        `/accounts/${encodeURIComponent(accountId ?? '')}/contacts?${qs}`,
      );
    },
  });
}
